/**
 * API Client Configuration
 * Centralized configuration for the API client
 */

import type { ApiClientOptions } from './types';
import {
  authenticationInterceptor,
  requestIdInterceptor,
  customHeadersInterceptor,
  responseNormalizationInterceptor,
  errorStatusInterceptor,
  tokenRefreshInterceptor,
  errorNotificationInterceptor,
  rateLimitInterceptor,
} from './interceptors';

/**
 * Get API base URL from environment
 */
export function getApiBaseUrl(): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return baseUrl || 'http://localhost:3000';
}

/**
 * Get API timeout from environment
 */
export function getApiTimeout(): number {
  const timeout = import.meta.env.VITE_API_TIMEOUT as string | undefined;
  return timeout ? Number(timeout) : 30000;
}

/**
 * Get API configuration
 */
export function getApiConfig(): ApiClientOptions {
  return {
    baseUrl: getApiBaseUrl(),
    timeout: getApiTimeout(),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    enableLogging: import.meta.env.DEV,
    enableRetry: true,
    defaultRetries: 3,
    defaultRetryDelay: 1000,
    // Request interceptors (executed in order)
    requestInterceptors: [
      authenticationInterceptor,
      requestIdInterceptor,
      customHeadersInterceptor,
    ],
    // Response interceptors (executed in order)
    responseInterceptors: [responseNormalizationInterceptor],
    // Error interceptors (executed in order)
    errorInterceptors: [
      errorStatusInterceptor,
      rateLimitInterceptor,
      tokenRefreshInterceptor,
      errorNotificationInterceptor,
    ],
  };
}

/**
 * API base URL
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * API timeout in milliseconds
 */
export const API_TIMEOUT = getApiTimeout();

/**
 * Validate API environment variables and log warnings
 */
function validateApiEnv() {
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const isProd = import.meta.env.PROD;

  if (!envBaseUrl) {
    console.warn(
      '[API Config] VITE_API_BASE_URL is not set — falling back to http://localhost:3000. ' +
        'Set it in your .env file.',
    );
  }

  if (isProd && API_BASE_URL.includes('localhost')) {
    console.error(
      '[API Config] VITE_API_BASE_URL points to localhost in a production build. ' +
        'This is likely a misconfiguration.',
    );
  }

  if (
    !API_BASE_URL.startsWith('http://') &&
    !API_BASE_URL.startsWith('https://')
  ) {
    console.error(
      `[API Config] VITE_API_BASE_URL must start with http:// or https:// — got: "${API_BASE_URL}"`,
    );
  }
}

validateApiEnv();

/**
 * Retry configuration
 */
export const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Rate limiting configuration
 */
export const RATE_LIMIT_CONFIG = {
  maxRequests: 100,
  windowMs: 60000, // 1 minute
};

/**
 * Cache configuration
 */
export const CACHE_CONFIG = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxSize: 100, // maximum number of cached items
};

/**
 * Authentication configuration
 */
export const AUTH_CONFIG = {
  tokenKey: 'auth_token',
  refreshTokenKey: 'refresh_token',
  userKey: 'auth_user',
  expirationKey: 'auth_expiration',
};
